/**
 * Bottom status bar (Obsidian-style). Shows ambient context info admins
 * always need: total entity counts, last sync, alert count. Click to
 * navigate to the relevant page.
 *
 * Hidden on mobile (≤600px) — the bottom tab bar already eats that space.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles, tokens } from '@fluentui/react-components';
import {
  People24Regular,
  Briefcase24Regular,
  Apps24Regular,
  ArrowSync24Regular,
  Warning24Regular,
} from '@fluentui/react-icons';
import { useAppContext } from '@/store/appContext';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  Employee,
  Project,
  AppAssignment,
  CloudSpend,
  CostAllocation,
  DeviceEnrollment,
} from '@/models/types';

const useStyles = makeStyles({
  bar: {
    position: 'fixed' as const,
    bottom: 0,
    left: '260px', // sidebar width
    right: 0,
    height: '24px',
    background: tokens.colorNeutralBackground2,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '0 16px',
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    zIndex: 50,
    fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
    '@media (max-width: 768px)': { display: 'none' },
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    cursor: 'pointer',
    padding: '0 4px',
    borderRadius: '4px',
    '&:hover': { background: tokens.colorNeutralBackground3 },
  },
  alert: {
    color: tokens.colorPaletteYellowForeground2,
  },
  spacer: {
    flex: 1,
  },
});

export function StatusBar() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { listIds, currentUser } = useAppContext();

  const { items: employees } = useSharePointList<Employee>('OC_Employees', { autoFetch: !!listIds });
  const { items: projects } = useSharePointList<Project>('OC_Projects', { autoFetch: !!listIds });
  const { items: assignments } = useSharePointList<AppAssignment>('OC_AppAssignments', { autoFetch: !!listIds });
  const { items: cloudSpend } = useSharePointList<CloudSpend>('OC_CloudSpend', { autoFetch: !!listIds });
  const isAdmin = currentUser?.role === 'Admin';
  const { items: allocs } = useSharePointList<CostAllocation>('OC_CostAllocations', {
    autoFetch: isAdmin && !!listIds?.OC_CostAllocations,
  });
  const { items: devices } = useSharePointList<DeviceEnrollment>('OC_Devices', {
    autoFetch: isAdmin && !!listIds?.OC_Devices,
  });

  const stats = useMemo(() => {
    const activeEmp = employees.filter((e) => e.fields.Status === 'Active').length;
    const activeProj = projects.filter((p) => p.fields.Status === 'Active').length;
    const activeAssign = assignments.filter((a) => a.fields.Status === 'Active').length;

    // Most recent CloudSpend timestamp ≈ last sync
    const sortedTs = cloudSpend
      .map((s) => (s.fields as { Timestamp?: string }).Timestamp)
      .filter((t): t is string => Boolean(t))
      .sort();
    const lastSyncTs = sortedTs.length ? sortedTs[sortedTs.length - 1] : undefined;

    // Alert count
    let alerts = 0;
    if (isAdmin) {
      // Pending devices
      alerts += devices.filter((d) => d.fields.Status === 'Pending').length;
      // Subs not allocated
      const subsWithSpend = new Set(cloudSpend.map((s) => s.fields.SubscriptionId).filter(Boolean));
      const allocBySub = new Map<string, number>();
      for (const a of allocs) {
        if (a.fields.ResourceType !== 'Subscription') continue;
        allocBySub.set(
          a.fields.ResourceId,
          (allocBySub.get(a.fields.ResourceId) ?? 0) + (a.fields.AllocationPercent || 0),
        );
      }
      for (const sub of subsWithSpend) {
        if ((allocBySub.get(sub) ?? 0) !== 100) alerts += 1;
      }
    }

    return { activeEmp, activeProj, activeAssign, lastSyncTs, alerts };
  }, [employees, projects, assignments, cloudSpend, allocs, devices, isAdmin]);

  return (
    <div className={styles.bar}>
      <div className={styles.item} onClick={() => navigate('/members')}>
        <People24Regular style={{ fontSize: 12 }} />
        <span>{stats.activeEmp}</span>
      </div>
      <div className={styles.item} onClick={() => navigate('/projects')}>
        <Briefcase24Regular style={{ fontSize: 12 }} />
        <span>{stats.activeProj}</span>
      </div>
      <div className={styles.item} onClick={() => navigate('/access')}>
        <Apps24Regular style={{ fontSize: 12 }} />
        <span>{stats.activeAssign} assignments</span>
      </div>
      {stats.lastSyncTs && (
        <div
          className={styles.item}
          onClick={() => navigate('/integrations')}
          title={`Last sync: ${stats.lastSyncTs}`}
        >
          <ArrowSync24Regular style={{ fontSize: 12 }} />
          <span>{relativeTime(stats.lastSyncTs)}</span>
        </div>
      )}
      <div className={styles.spacer} />
      {isAdmin && stats.alerts > 0 && (
        <div className={`${styles.item} ${styles.alert}`} onClick={() => navigate('/')}>
          <Warning24Regular style={{ fontSize: 12 }} />
          <span>{stats.alerts} alert{stats.alerts > 1 ? 's' : ''}</span>
        </div>
      )}
      <span style={{ opacity: 0.6 }}>⌘K to search</span>
    </div>
  );
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
